import { useState } from 'react';
import { useT } from '@/i18n';
import { describeError } from '@/llm/types';
import { lookupFundNav, type NavLookup } from '@/market/navLookup';
import { formatUsd } from '@/reports/cost';
import { hasLLM, useSettings } from '@/settings/settingsStore';
import type { Asset } from '@/types';

function host(url?: string): string {
  try {
    return url ? new URL(url).hostname.replace(/^www\./, '') : '';
  } catch {
    return url ?? '';
  }
}

/** "Look up NAV online" button with its status line; hands the result to the caller, who decides how to apply it. */
export function NavLookupButton({ fund, onResult, disabled }: { fund: Pick<Asset, 'symbol' | 'name' | 'market' | 'currency'>; onResult: (r: NavLookup) => void; disabled?: boolean }) {
  const { t } = useT();
  const settings = useSettings();
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<{ ok: boolean; text: string } | null>(null);
  const ready = hasLLM(settings) && !!fund.symbol.trim();

  const run = async () => {
    setBusy(true);
    setStatus(null);
    try {
      const r = await lookupFundNav(fund);
      setStatus({ ok: true, text: t('nav.found', { nav: r.nav, ccy: r.currency ?? fund.currency.toUpperCase(), date: r.asOf, source: host(r.source) || '—', cost: r.costUsd != null ? ` · ${formatUsd(r.costUsd)}` : '' }) });
      onResult(r);
    } catch (e) {
      setStatus({ ok: false, text: describeError(e) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
      <button type="button" className="btn quiet" disabled={disabled || busy || !ready} title={!hasLLM(settings) ? t('nav.needLLM') : undefined} onClick={() => void run()}>
        {busy ? t('nav.looking') : t('nav.lookup')}
      </button>
      {status && <span className={status.ok ? 'muted' : 'down'} style={{ fontSize: 12 }}>{status.text}</span>}
    </span>
  );
}
